// ########################################################## //
// #                                                        # //
// #        A Post Processing Stack that can render         # //
// #        multiple shaders over any existing image        # //
// #         including canvas, ofscreen buffers and         # //
// #                     classing images.                   # //
// #                                                        # //
// #           A post processing shader has to have         # //
// #           a uniform of the type sampler2D named        # //
// #           u_mainTex which gets passed the source       # //
// #                   image for the effect                 # //
// #                                                        # //
// ########################################################## //
// #                                                        # //
// #                Written by Fabian Kober                 # //
// #                  fabian-kober@gmx.net                  # //
// #                                                        # //
// ########################################################## //

const SKIP_EFFECTS = false;

export default class PostProcessingStack {
    /*
    example shader source:
    let src = [
        {
            'effect': 'effect name',
            'vert': some vertex shader code,
            'frag': some fragment shader code
        },
        {
            'effect': 'effect name 2',
            'vert': some vertex shader code,
            'frag': some fragment shader code
        }
    ];
     */

    // creates a Post Processing stack for the given source with the provided effects. The effects are provided via an array
    // containing one Javascript Object for each effect with a name, a vertex and a fragment shader (see example above)
    constructor(p5, imageSource, shaderSources, width, height) {
        this.p5 = p5;
        this.width = width;
        this.height = height;

        // save reference to the image source
        this.source = imageSource;

        // create the buffer
        this.buffer = p5.createGraphics(width, height, p5.WEBGL);

        // compile all the shaders and save them in a Javascript Object so we can reference them by name
        this.shaders = {};
        for (let i = 0; i < shaderSources.length; ++i) {
            this.shaders[shaderSources[i].effect] = this.buffer.createShader(shaderSources[i].vert, shaderSources[i].frag);
        }
    }

    // adds an effect with the provided name and vertex and fragment shader to the end of the stack
    addEffect(effectName, vertSource, fragSource) {
        this.shaders[effectName] = this.buffer.createShader(vertSource, fragSource);
    }


    // applys all effects in the order they where passed into the constructor and renders the buffer on the canvas when it is done
    applyEffects() {
        // our source for the first effect is the raw image
        let src = this.source;

        if(SKIP_EFFECTS) {
            this.p5.imageMode(this.p5.CORNER);
            this.p5.image(src, 0, 0);
            return;
        }

        // go through all effects and render them
        for (const effectName of Object.keys(this.shaders)) {
            // get the shader
            let sh = this.shaders[effectName];

            // set the shader and give it its source image
            this.buffer.shader(sh);
            sh.setUniform('u_mainTex', src);

            // render the effect
            this.buffer.quad(-1, -1, -1, 1, 1, 1, 1, -1);

            // the source for the next effect is the buffer
            src = this.buffer;
        }

        // draw the buffer on the screen
        this.p5.imageMode(this.p5.CORNER);
        // this.p5.tint(255, 150);
        this.p5.image(this.buffer, 0, 0);
    }


    // sets the given uniform in the effect
    setUniform(effect, uniform, value) {
        // get the shader and activate it
        let sh = this.shaders[effect];
        this.buffer.shader(sh);

        // set the uniform
        sh.setUniform(uniform, value);
    }

    /*
    example uniforms:
    let uniforms = {
        'uniformName1': some value,
        'uniformName2': some value
    };
     */

    // sets multiple uniforms of an effect at once. The uniforms are provided in a Javascript Object (see example above)
    setUniforms(effect, uniforms) {
        // get the shader and activate it
        let sh = this.shaders[effect];
        this.buffer.shader(sh);

        // set the uniforms
        for (const uniformName of Object.keys(uniforms)) {
            sh.setUniform(uniformName, uniforms[uniformName]);
        }
    }
}
